import { useEffect, useState, useRef } from "react";
import api, { fmtNum, API } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Upload, Download, Search, Pencil, Trash2, Plus, X, FileSpreadsheet } from "lucide-react";
import CategoryCombobox from "@/components/CategoryCombobox";

const empty = {
    name: "", hsn: "", mrp: 0, b2b_price: 0, b2c_price: 0, our_price: 0, tax_percent: 0,
    category: "Uncategorized", unit: "Pack", active: true,
};

export default function Items() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [cats, setCats] = useState([]);
    const [cat, setCat] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const [suggesting, setSuggesting] = useState(false);
    const fileRef = useRef();

    const load = () =>
        api.get("/items", { params: { q, category: cat } }).then((r) => setItems(r.data));

    useEffect(() => { load(); }, [q, cat]);
    useEffect(() => { api.get("/items/categories").then((r) => setCats(r.data)); }, [items.length]);

    useEffect(() => {
        if (!showForm) return;
        const onKey = (e) => { if (e.key === "Escape") setShowForm(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showForm]);

    const openNew = () => { setForm(empty); setEditingId(null); setShowForm(true); };
    const openEdit = (it) => { setForm(it); setEditingId(it.id); setShowForm(true); };

    const save = async () => {
        if (!form.name || !form.hsn) return toast.error("Name and HSN are required");
        try {
            if (editingId) await api.put(`/items/${editingId}`, form);
            else await api.post("/items", form);
            toast.success(editingId ? "Item updated" : "Item added");
            setShowForm(false); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const del = async (id) => {
        if (!confirm("Delete this item?")) return;
        await api.delete(`/items/${id}`);
        toast.success("Deleted"); load();
    };

    const suggest = async () => {
        if (!form.name) return toast.error("Enter item name first");
        setSuggesting(true);
        try {
            const r = await api.post("/ai/suggest-category", { name: form.name, hsn: form.hsn });
            setForm({ ...form, category: r.data.suggestion });
            toast.success(`AI suggested: ${r.data.suggestion}`);
        } catch (e) { toast.error("AI suggestion failed"); }
        setSuggesting(false);
    };

    const upload = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        const fd = new FormData(); fd.append("file", f);
        try {
            const r = await api.post("/items/bulk-import", fd);
            toast.success(`${r.data.inserted} added, ${r.data.updated} updated (of ${r.data.total} rows)`);
            load();
        } catch { toast.error("Import failed"); }
        e.target.value = "";
    };

    return (
        <div data-testid="items-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Item Master</h1>
                    <p className="text-slate-500 mt-1 text-sm">Manage products, HSN codes and GST rates</p>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                    <a href={`${API}/items/export.xlsx`} download className="hdb-btn hdb-btn-ghost"
                       data-testid="download-xlsx-btn">
                        <FileSpreadsheet size={14} /> Download Excel
                    </a>
                    <a href={`${API}/items/export.csv`} download className="hdb-btn hdb-btn-ghost"
                       data-testid="download-csv-btn">
                        <Download size={14} /> Download CSV
                    </a>
                    <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" onChange={upload} className="hidden" data-testid="csv-input" />
                    <button className="hdb-btn hdb-btn-mint" onClick={() => fileRef.current.click()} data-testid="import-csv-btn">
                        <Upload size={14} /> Upload Rates
                    </button>
                    <button className="hdb-btn hdb-btn-primary" onClick={openNew} data-testid="add-item-btn">
                        <Plus size={14} /> Add Item
                    </button>
                </div>
            </div>

            <div className="hdb-card mb-5 p-4 flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                    <input className="hdb-input pl-9" placeholder="Search by name or HSN…" value={q}
                           onChange={(e) => setQ(e.target.value)} data-testid="item-search" />
                </div>
                <select className="hdb-input max-w-[260px]" value={cat} onChange={(e) => setCat(e.target.value)}
                        data-testid="cat-filter">
                    <option value="">All categories</option>
                    {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            <div className="hdb-card overflow-hidden">
                <table className="hdb-table w-full">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>HSN</th>
                            <th>Category</th>
                            <th>Unit</th>
                            <th className="text-right">MRP</th>
                            <th className="text-right">B2B Price</th>
                            <th className="text-right">B2C Price</th>
                            <th className="text-right">GST %</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it) => (
                            <tr key={it.id} data-testid={`item-row-${it.id}`}>
                                <td className="font-medium text-slate-900">{it.name}</td>
                                <td className="hdb-num text-slate-600">{it.hsn}</td>
                                <td><span className="hdb-tag hdb-tag-slate">{it.category}</span></td>
                                <td className="text-slate-600">{it.unit}</td>
                                <td className="hdb-num hdb-num-right">{fmtNum(it.mrp)}</td>
                                <td className="hdb-num hdb-num-right font-semibold">{fmtNum(it.b2b_price)}</td>
                                <td className="hdb-num hdb-num-right font-semibold text-emerald-700">{fmtNum(it.b2c_price)}</td>
                                <td className="hdb-num hdb-num-right">{it.tax_percent}%</td>
                                <td className="text-right whitespace-nowrap">
                                    <button className="hdb-btn hdb-btn-ghost" onClick={() => openEdit(it)}
                                            data-testid={`edit-item-${it.id}`}>
                                        <Pencil size={13} />
                                    </button>
                                    <button className="hdb-btn hdb-btn-danger ml-2" onClick={() => del(it.id)}
                                            data-testid={`delete-item-${it.id}`}>
                                        <Trash2 size={13} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={9} className="text-center py-12 text-slate-400">No items found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-start justify-center pt-16 no-print"
                     onClick={() => setShowForm(false)}>
                    <div className="hdb-card w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="item-form">
                        <div className="flex justify-between items-start mb-4">
                            <h2 className="hdb-heading font-bold text-xl">
                                {editingId ? "Edit Item" : "Add New Item"}
                            </h2>
                            <button className="hdb-btn hdb-btn-ghost" onClick={() => setShowForm(false)}
                                    data-testid="form-close" aria-label="Close">
                                <X size={14} />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="hdb-label">Item Name</label>
                                <input className="hdb-input" value={form.name}
                                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                                       data-testid="form-name" />
                            </div>
                            <div>
                                <label className="hdb-label">HSN Code</label>
                                <input className="hdb-input hdb-mono" value={form.hsn}
                                       onChange={(e) => setForm({ ...form, hsn: e.target.value })}
                                       data-testid="form-hsn" />
                            </div>
                            <div>
                                <label className="hdb-label">Unit</label>
                                <input className="hdb-input" value={form.unit}
                                       onChange={(e) => setForm({ ...form, unit: e.target.value })} />
                            </div>
                            <div>
                                <label className="hdb-label">MRP (₹)</label>
                                <input className="hdb-input hdb-mono" type="number" value={form.mrp}
                                       onChange={(e) => setForm({ ...form, mrp: parseFloat(e.target.value || 0) })} />
                            </div>
                            <div>
                                <label className="hdb-label">GST %</label>
                                <input className="hdb-input hdb-mono" type="number" value={form.tax_percent}
                                       onChange={(e) => setForm({ ...form, tax_percent: parseFloat(e.target.value || 0) })}
                                       data-testid="form-gst" />
                            </div>
                            <div>
                                <label className="hdb-label">B2B Price (₹)</label>
                                <input className="hdb-input hdb-mono" type="number" value={form.b2b_price}
                                       onChange={(e) => setForm({ ...form, b2b_price: parseFloat(e.target.value || 0), our_price: parseFloat(e.target.value || 0) })}
                                       data-testid="form-b2b-price" />
                            </div>
                            <div>
                                <label className="hdb-label">B2C Price (₹)</label>
                                <input className="hdb-input hdb-mono" type="number" value={form.b2c_price}
                                       onChange={(e) => setForm({ ...form, b2c_price: parseFloat(e.target.value || 0) })}
                                       data-testid="form-b2c-price" />
                            </div>
                            <div>
                                <label className="hdb-label">Category</label>
                                <div className="flex gap-2 items-start">
                                    <div className="flex-1">
                                        <CategoryCombobox
                                            value={form.category}
                                            onChange={(v) => setForm({ ...form, category: v })}
                                            options={cats}
                                            placeholder="Type or pick a category…"
                                            testId="form-category" />
                                    </div>
                                    <button type="button" onClick={suggest} disabled={suggesting}
                                            className="hdb-btn hdb-btn-ghost whitespace-nowrap"
                                            data-testid="ai-suggest-btn">
                                        <Sparkles size={13} /> {suggesting ? "…" : "AI"}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button className="hdb-btn hdb-btn-ghost" onClick={() => setShowForm(false)}
                                    data-testid="form-cancel">Cancel</button>
                            <button className="hdb-btn hdb-btn-primary" onClick={save} data-testid="form-save">
                                {editingId ? "Update" : "Add Item"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
